import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Checkbox,
  IconButton,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  useTheme,
  alpha,
  Grow,
  Slide,
  Zoom,
  Paper,
  LinearProgress,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Task as TaskIcon,
  TrendingUp as TrendingUpIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';

import GitHubIcon from '@mui/icons-material/GitHub';
import { ILogo } from '../../styles/components/ILogo';
interface Task {
  id: string;
  text: string;
  completed: boolean;
  createdAt: Date;
  completedAt?: Date;
}



const completedAnimation = keyframes`
  0% { transform: scale(1) rotateZ(0deg); }
  50% { transform: scale(1.2) rotateZ(180deg); }
  100% { transform: scale(1) rotateZ(360deg); }
`;

const cardFlipAnimation = keyframes`
  0% { transform: perspective(1000px) rotateY(0deg); }
  50% { transform: perspective(1000px) rotateY(90deg); }
  100% { transform: perspective(1000px) rotateY(0deg); }
`;

const motivationSlideIn = keyframes`
  0% { 
    opacity: 0; 
    transform: translateX(-50px) scale(0.8); 
  }
  100% { 
    opacity: 1; 
    transform: translateX(0) scale(1); 
  }
`;

const iconBounce = keyframes`
  0%, 100% { 
    transform: translateY(0) rotate(0deg) scale(1); 
  }
  25% { 
    transform: translateY(-10px) rotate(5deg) scale(1.1); 
  }
  50% { 
    transform: translateY(0) rotate(0deg) scale(1.2); 
  }
  75% { 
    transform: translateY(-5px) rotate(-5deg) scale(1.1); 
  }
`;

const glowPulse = keyframes`
  0% { 
    box-shadow: 0 0 10px rgba(255, 215, 0, 0.3); 
  }
  50% { 
    box-shadow: 0 0 25px rgba(255, 215, 0, 0.6), 0 0 35px rgba(255, 107, 107, 0.4); 
  }
  100% { 
    box-shadow: 0 0 10px rgba(255, 215, 0, 0.3); 
  }
`;

const DailyTasks: React.FC = () => {
  const theme = useTheme();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [open, setOpen] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [completedTasks, setCompletedTasks] = useState(0);
  const [motivationIndex, setMotivationIndex] = useState(0);

  const motivationalMessages = [
    { text: "You've got this! 💪", icon: "🌟" },
    { text: "Every task completed is a victory! 🏆", icon: "🎯" },
    { text: "Progress, not perfection! ✨", icon: "🚀" },
    { text: "Your future self will thank you! 🙏", icon: "💫" },
    { text: "Small steps lead to big changes! 👣", icon: "🌈" },
    { text: "Today is your day to shine! ☀️", icon: "✨" },
    { text: "Believe in yourself and take action! 💝", icon: "🎊" },
    { text: "You're capable of amazing things! 🦋", icon: "🌺" },
  ];

  // Load tasks from localStorage on component mount
  useEffect(() => {
    const savedTasks = localStorage.getItem('dailyTasks');
    if (savedTasks) {
      const parsedTasks = JSON.parse(savedTasks).map((task: any) => ({
        ...task,
        createdAt: new Date(task.createdAt),
        completedAt: task.completedAt ? new Date(task.completedAt) : undefined,
      }));
      setTasks(parsedTasks);
      setCompletedTasks(parsedTasks.filter((task: Task) => task.completed).length);
    }
  }, []);



  // Save tasks to localStorage whenever tasks change
  useEffect(() => {
    localStorage.setItem('dailyTasks', JSON.stringify(tasks));
    setCompletedTasks(tasks.filter(task => task.completed).length);
  }, [tasks]);

  // Rotate motivational messages
  useEffect(() => {
    const interval = setInterval(() => {
      setMotivationIndex((prev) => (prev + 1) % motivationalMessages.length);
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [motivationalMessages.length]);

  const addTask = () => {
    if (newTask.trim()) {
      const task: Task = {
        id: Date.now().toString(),
        text: newTask.trim(),
        completed: false,
        createdAt: new Date(),
      };
      setTasks([...tasks, task]);
      setNewTask('');
      setOpen(false);
    }
  };

  const toggleTask = (id: string) => {
    setTasks(tasks.map(task => 
      task.id === id 
        ? { 
            ...task, 
            completed: !task.completed,
            completedAt: !task.completed ? new Date() : undefined
          }
        : task
    ));
  };

  const deleteTask = (id: string) => {
    setTasks(tasks.filter(task => task.id !== id));
  };

  const clearCompletedTasks = () => {
    setTasks(tasks.filter(task => !task.completed));
  };

  const exportToCSV = () => {
    if (tasks.length === 0) {
      alert('No tasks to export!');
      return;
    }

    // Create CSV header
    const headers = ['Task', 'Status', 'Created Date', 'Completed Date', 'Progress'];
    
    // Create CSV data
    const csvData = tasks.map(task => {
      const createdDate = task.createdAt.toLocaleDateString();
      const completedDate = task.completedAt ? task.completedAt.toLocaleDateString() : 'Not completed';
      const status = task.completed ? 'Completed ✅' : 'Pending ⏳';
      const progress = task.completed ? '100%' : '0%';
      
      return [
        `"${task.text}"`,
        status,
        createdDate,
        completedDate,
        progress
      ].join(',');
    });

    // Combine headers and data
    const csvContent = [headers.join(','), ...csvData].join('\n');
    
    // Add summary at the end
    const summaryData = [
      '',
      '=== DAILY SUMMARY ===',
      `Total Tasks: ${tasks.length}`,
      `Completed: ${completedTasks}`,
      `Pending: ${tasks.length - completedTasks}`,
      `Progress: ${Math.round(progress)}%`,
      `Report Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
    ].join('\n');

    const finalCSV = csvContent + '\n' + summaryData;
    
    // Create and download file
    const blob = new Blob([finalCSV], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const today = new Date().toISOString().split('T')[0];
    
    link.setAttribute('href', url);
    link.setAttribute('download', `daily-tasks-${today}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyToClipboard = async () => {
    if (tasks.length === 0) {
      alert('No tasks to copy!');
      return;
    }

    // Create formatted text for clipboard
    const tasksList = tasks.map(task => {
      const status = task.completed ? '✅ Completed' : '⏳ Pending';
      const createdDate = task.createdAt.toLocaleDateString();
      const completedDate = task.completedAt ? ` | Completed: ${task.completedAt.toLocaleDateString()}` : '';
      
      return `${status} | ${task.text} | Created: ${createdDate}${completedDate}`;
    }).join('\n');

    const summary = `
📋 DAILY TASKS SUMMARY
────────────────────────────────
${tasksList}

📊 PROGRESS OVERVIEW
────────────────────────────────
📈 Total Tasks: ${tasks.length}
✅ Completed: ${completedTasks}
⏳ Pending: ${tasks.length - completedTasks}
📈 Progress: ${Math.round(progress)}%
📅 Report Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}

Generated by Daily Tasks Bucket 🎯`;

    try {
      await navigator.clipboard.writeText(summary);
      alert('Task list copied to clipboard! 📋\nYou can now paste it anywhere.');
    } catch (err) {
      console.error('Failed to copy: ', err);
      alert('Failed to copy to clipboard. Please try the CSV export instead.');
    }
  };

  const progress = tasks.length > 0 ? (completedTasks / tasks.length) * 100 : 0;
  const pendingTasks = tasks.filter(task => !task.completed);
  const doneTasks = tasks.filter(task => task.completed);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <IconButton
          href="https://github.com/release-pilot/release-pilot-public/tree/master/daily-task-bucketlist"
          target="_blank"
          rel="noopener"
          color="primary"
          style={{ position: 'absolute', top: 0, right: 0, margin: '10px' }}
        >
          <GitHubIcon fontSize="large" />
        </IconButton>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Grid sx={{ paddingTop: 2, alignContent: 'center' }}>
        <center>
          <ILogo src="/assets/Single_mainlogo_tm.svg" />
        </center>
      </Grid>
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Daily Tasks Bucket
          </Typography>
          
          {/* Motivational Message */}
          <Box
            sx={{
              my: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              minHeight: '60px',
            }}
          >
            <Typography
              sx={{
                fontSize: '2rem',
                animation: `${iconBounce} 3s ease-in-out infinite`,
              }}
            >
              {motivationalMessages[motivationIndex].icon}
            </Typography>
            <Box
              sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                borderRadius: '25px',
                px: 4,
                py: 2,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                animation: `${glowPulse} 3s ease-in-out infinite`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
                  animation: 'shimmer 2s infinite',
                },
                '@keyframes shimmer': {
                  '0%': { left: '-100%' },
                  '100%': { left: '100%' },
                },
              }}
            >
              <Typography
                variant="h6"
                key={motivationIndex}
                sx={{
                  fontWeight: 600,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.warning.main})`,
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: `${motivationSlideIn} 0.8s ease-out`,
                  textAlign: 'center',
                }}
              >
                {motivationalMessages[motivationIndex].text}
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="h6" color="text.secondary">
            Organize your day, achieve your goals
          </Typography>
        </Box>

        {/* Progress Card */}
        <Card
          sx={{
            mb: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TrendingUpIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Today's Progress
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '& .MuiLinearProgress-bar': {
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  borderRadius: 5,
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  icon={<TaskIcon />}
                  label={`${tasks.length} Total`}
                  variant="outlined"
                  sx={{ borderColor: theme.palette.primary.main }}
                />
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${completedTasks} Completed`}
                  color="success"
                  variant="outlined"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {tasks.length > 0 && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ShareIcon />}
                      onClick={copyToClipboard}
                      sx={{
                        color: theme.palette.secondary.main,
                        borderColor: theme.palette.secondary.main,
                        '&:hover': {
                          borderColor: theme.palette.secondary.dark,
                          backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                          transform: 'translateY(-1px)',
                          transition: 'all 0.3s ease',
                        },
                      }}
                    >
                      Copy List
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={exportToCSV}
                      sx={{
                        color: theme.palette.primary.main,
                        borderColor: theme.palette.primary.main,
                        '&:hover': {
                          borderColor: theme.palette.primary.dark,
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          transform: 'translateY(-1px)',
                          transition: 'all 0.3s ease',
                        },
                      }}
                    >
                      Export CSV
                    </Button>
                  </>
                )}
                {completedTasks > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={clearCompletedTasks}
                    sx={{
                      color: theme.palette.error.main,
                      borderColor: theme.palette.error.main,
                      '&:hover': {
                        borderColor: theme.palette.error.dark,
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                      },
                    }}
                  >
                    Clear Completed
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Pending Tasks */}
        {pendingTasks.length > 0 && (
          <Card
            sx={{
              mb: 4,
              background: alpha(theme.palette.background.paper, 0.9),
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.warning.main }}>
                Pending Tasks ({pendingTasks.length})
              </Typography>
              <List>
                {pendingTasks.map((task, index) => (
                  <Grow in={true} timeout={300 + index * 100} key={task.id}>
                    <ListItem
                      sx={{
                        mb: 1,
                        background: alpha(theme.palette.warning.main, 0.05),
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        animation: `${cardFlipAnimation} 0.6s ease-in-out`,
                        '&:hover': {
                          transform: 'translateX(10px) scale(1.02)',
                          transition: 'all 0.3s ease',
                          boxShadow: theme.shadows[4],
                        },
                      }}
                    >
                      <ListItemButton onClick={() => toggleTask(task.id)}>
                        <Checkbox
                          icon={<RadioButtonUncheckedIcon />}
                          checkedIcon={<CheckCircleIcon />}
                          checked={task.completed}
                          sx={{
                            color: theme.palette.warning.main,
                            '&.Mui-checked': {
                              color: theme.palette.success.main,
                              animation: `${completedAnimation} 0.6s ease-in-out`,
                            },
                          }}
                        />
                        <ListItemText
                          primary={task.text}
                          secondary={`Created: ${task.createdAt.toLocaleDateString()}`}
                        />
                      </ListItemButton>
                      <IconButton
                        onClick={() => deleteTask(task.id)}
                        sx={{
                          color: theme.palette.error.main,
                          '&:hover': {
                            transform: 'scale(1.2) rotate(90deg)',
                            transition: 'all 0.3s ease',
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  </Grow>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {pendingTasks.length === 0 && (
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
            }}
          >
            <TaskIcon sx={{ fontSize: 80, color: theme.palette.primary.main, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {tasks.length === 0 ? 'No tasks yet!' : 'All tasks completed!'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {tasks.length === 0 
                ? 'Start your productive day by adding your first task' 
                : 'Great job! Add more tasks to keep the momentum going'}
            </Typography>
          </Paper>
        )}

        {/* Give Back Section */}
        <Card
          sx={{
            mt: 4,
            mb: 6,
            background: `linear-gradient(135deg, ${alpha('#FFD700', 0.1)}, ${alpha(theme.palette.success.main, 0.1)})`,
            border: `1px solid ${alpha('#FFD700', 0.3)}`,
            borderRadius: '15px',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: `linear-gradient(90deg, #FFD700, ${theme.palette.success.main}, #FFD700)`,
            },
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(45deg, #FFD700, ${theme.palette.success.main})`,
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                💛 Donate Back – Not Just Money
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  mb: 1,
                }}
              >
                If Release Pilot boosted your productivity by 0.1%—give back through <strong>impact, not money</strong> 🌟
              </Typography>
            </Box>

            <Box 
              sx={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 1,
                mb: 2,
              }}
            >
              {['🤝 Help others', '👥 Mentor fellow developer', '🚀 Share opportunities', '💡 Contribute skills', '❤️ Spread kindness'].map((item, index) => (
                <Chip
                  key={index}
                  label={item}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: alpha('#FFD700', 0.5),
                    color: theme.palette.text.primary,
                    '&:hover': {
                      backgroundColor: alpha('#FFD700', 0.1),
                      transform: 'scale(1.02)',
                    },
                  }}
                />
              ))}
            </Box>

            <Typography 
              variant="body2" 
              sx={{ 
                textAlign: 'center',
                color: theme.palette.text.secondary,
                fontStyle: 'italic',
              }}
            >
              Every small action matters. Let your gratitude become someone else's breakthrough. 🙌
            </Typography>
          </CardContent>
        </Card>

        {/* Crafted with Love */}
        <Typography 
          variant="body1" 
          sx={{ 
            textAlign: 'center',
            color: theme.palette.text.secondary,
            mt: 3,
            mb: 2,
            fontSize: '1.1rem',
          }}
        >
          Crafted with ❤️ in India
        </Typography>

        {/* Floating Action Button */}
        <Zoom in={true} timeout={500}>
          <Fab
            color="primary"
            aria-label="add task"
            onClick={() => setOpen(true)}
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              '&:hover': {
                transform: 'scale(1.1) rotate(45deg)',
                transition: 'all 0.3s ease',
              },
            }}
          >
            <AddIcon />
          </Fab>
        </Zoom>

        {/* Add Task Dialog */}
        <Dialog 
          open={open} 
          onClose={() => setOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: '#ffffff',
              backdropFilter: 'blur(15px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.3)}, 0 0 60px ${alpha(theme.palette.secondary.main, 0.2)}`,
              border: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
              borderRadius: '20px',
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.warning.main}, ${theme.palette.success.main})`,
              },
            }
          }}
        >
          <DialogTitle>
            <Box sx={{ 
              textAlign: 'center', 
              py: 2,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '3px',
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                borderRadius: '2px',
              }
            }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                  mb: 1,
                }}
              >
                ✨ Add New Task ✨
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  fontStyle: 'italic',
                }}
              >
                Let's make today productive!
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ px: 4, py: 3 }}>
            <TextField
              autoFocus
              margin="dense"
              label="🎯 What's your next achievement?"
              fullWidth
              variant="outlined"
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  addTask();
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  borderRadius: '15px',
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.2)}`,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease',
                  },
                  '&.Mui-focused': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: '3px',
                    boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.3)}`,
                    transform: 'translateY(-3px)',
                  },
                  '& fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  '&.Mui-focused': {
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                  },
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 4, py: 3, gap: 2 }}>
            <Button 
              onClick={() => setOpen(false)} 
              sx={{
                borderRadius: '25px',
                px: 3,
                py: 1.5,
                fontWeight: 600,
                color: theme.palette.text.secondary,
                border: `2px solid ${alpha(theme.palette.text.secondary, 0.3)}`,
                '&:hover': {
                  border: `2px solid ${alpha(theme.palette.text.secondary, 0.6)}`,
                  backgroundColor: alpha(theme.palette.text.secondary, 0.05),
                  transform: 'translateY(-1px)',
                  transition: 'all 0.3s ease',
                },
              }}
            >
              Maybe Later
            </Button>
            <Button
              onClick={addTask}
              variant="contained"
              disabled={!newTask.trim()}
              sx={{
                borderRadius: '25px',
                px: 4,
                py: 1.5,
                fontWeight: 700,
                fontSize: '1.1rem',
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.warning.main})`,
                boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark}, ${theme.palette.warning.dark})`,
                  boxShadow: `0 12px 35px ${alpha(theme.palette.primary.main, 0.6)}`,
                  transform: 'translateY(-3px) scale(1.02)',
                  transition: 'all 0.3s ease',
                },
                '&:disabled': {
                  background: alpha(theme.palette.text.disabled, 0.3),
                  boxShadow: 'none',
                  transform: 'none',
                },
              }}
            >
              🚀 Let's Do This!
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default DailyTasks; 